import { ArrowLeft, FileText, Footprints, Eye, Droplet, Heart, Activity, ThermometerSun, Calendar, Wind, MessageSquare, FlaskConical, AlertCircle, Home, Dumbbell, Edit2, Trash2, Mail, Phone, User, TrendingUp, MapPin, Plus, Pill, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { ClinicalSummaryTab } from './ClinicalSummaryTab';
import { SixMWTReportView } from './SixMWTReportView';
import { LabResultsTrends } from './doctor/LabResultsTrends';
import { FEV1TrendsCard } from './doctor/FEV1TrendsCard';
import { SixMWDTrendsCard } from './doctor/SixMWDTrendsCard';
import { HomeNurseTasksView } from './doctor/HomeNurseTasksView';
import { HomeNurseTasksViewCompact } from './doctor/HomeNurseTasksViewCompact';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useVitalsTrends } from '../lib/hooks/useTrends';
import { useSixMWTHistory } from '../lib/hooks/useTests';
import { useDischargeSummaries } from '../lib/hooks/useDischargeSummaries';
import { useLabResults } from '../lib/hooks/useLabResults';
import { useClinicalNotes, useCreateClinicalNote, useUpdateClinicalNote, useDeleteClinicalNote } from '../lib/hooks/useClinicalNotes';
import { useSymptomHistory } from '../lib/hooks/useSymptoms';
import { useAuth } from '../lib/hooks/useAuth';
import { medicationsApi } from '../lib/api/medications';
import { EmptyState } from './common/EmptyState';
import { LoadingState } from './common/LoadingState';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { availableMemory } from 'process';
import { UserRole } from '@/lib/api';

interface PatientDetailsViewProps {
  patient: {
    id: string;
    name: string;
    age: number;
    gender?: string;
    cohortId: string;
    adherence: number;
    status: 'On Track' | 'Missed' | 'Escalated';
    lastSession: string;
    streak: number;
  };
  userRole : string;
  onBack: () => void;
  onScheduleAppointment?: (patientId: string, patientName: string) => void;
  onEditExercisePlan?: (patientId: string, patientName: string, planId?: string) => void;
}

// Medications state
interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  scheduled_times: string;
  notes?: string;
}

export function PatientDetailsView({ patient, userRole, onBack, onScheduleAppointment, onEditExercisePlan }: PatientDetailsViewProps) {
  
  // Fetch patient data from API
  const { data: dischargeSummariesData } = useDischargeSummaries(parseInt(patient.id));
  const { data: vitalsData } = useVitalsTrends(parseInt(patient.id));
  const { data: sixMWTData } = useSixMWTHistory(parseInt(patient.id));
  const { data: labResultsData } = useLabResults(parseInt(patient.id));
  const { data: symptomsData } = useSymptomHistory(parseInt(patient.id), 30);

  const [medications, setMedications] = useState<Medication[]>([]);

  const selectedPatient = {
    id: patient.id, 
    name: patient.name,
    age: patient.age,
    gender: patient.gender,
    hospital_name: patient.hospital_name,
  }
  
  // Get first discharge summary for this patient
  const dischargeSummary = dischargeSummariesData?.[0];

  // State for medication doses
  const [tacrolimusDose, setTacrolimusDose] = useState('2.5');
  const [everolimusDose, setEverolimusDose] = useState('1.0');
    
  // ✅ NEW: Get current user for permission checking
  const { user } = useAuth();
  
  // initialize from user if already present
  const [role, setRole] = useState(userRole);
 
  useEffect(() => {
  if (dischargeSummary?.medications) {
    const medications_ = dischargeSummary.medications.map(m => ({
      id : m.id,
      name: m.medication_name,
      dosage: m.dosage,
      frequency: m.frequency,
      notes: m.instructions,
      scheduled_times: m.scheduled_times
    }));

    setMedications(medications_);    
  }

  if (user?.role && user.role !== role) {
    setRole(user.role);
  }

}, [dischargeSummary, user]);
  
  // ✅ NEW: State for clinical notes
  const [note, setNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  
  // ✅ NEW: Clinical notes hooks
  const { data: clinicalNotesData, isLoading: notesLoading, error: notesError } = useClinicalNotes(parseInt(patient.id), true);
  const createNoteMutation = useCreateClinicalNote();
  const updateNoteMutation = useUpdateClinicalNote();
  const deleteNoteMutation = useDeleteClinicalNote();

  const [showAddMedicationForm, setShowAddMedicationForm] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [medicationForm, setMedicationForm] = useState({
    id: '',
    name: '',
    dosage: '',
    frequency: '',
    scheduled_times: '',
    notes: '',
  });
  
  // Get current drug levels from latest lab results
  const tacrolimusResult = labResultsData?.find(r => r.test_name?.toLowerCase().includes('tacrolimus'));
  const everolimusResult = labResultsData?.find(r => r.test_name?.toLowerCase().includes('everolimus'));
  const tacrolimusLevel = tacrolimusResult?.result_value || 'N/A';
  const everolimusLevel = everolimusResult?.result_value || 'N/A';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Missed':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'Escalated':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      default:
        return '';
    }
  };

  // ✅ Handler to save medication doses
  const handleSaveMedicationDoses = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Create Tacrolimus medication schedule
      if (tacrolimusDose && parseFloat(tacrolimusDose) > 0) {
        await medicationsApi.createMedicationSchedule({
          patient_id: parseInt(patient.id),
          medication_name: 'Tacrolimus',
          dosage: `${tacrolimusDose} mg`,
          frequency: 'BID (Twice daily)',
          category: 'Immunosuppressant',
          scheduled_times: ['08:00', '20:00'],
          instructions: 'Take with or without food. Maintain consistent timing.',
          start_date: today,
        });
      }
      
      // Create Everolimus medication schedule
      if (everolimusDose && parseFloat(everolimusDose) > 0) {
        await medicationsApi.createMedicationSchedule({
          patient_id: parseInt(patient.id),
          medication_name: 'Everolimus',
          dosage: `${everolimusDose} mg`,
          frequency: 'OD (Once daily)',
          category: 'Immunosuppressant',
          scheduled_times: ['08:00'],
          instructions: 'Take with or without food. Maintain consistent timing.',
          start_date: today,
        });
      }
      
      toast.success('Medication doses updated successfully!', {
        description: `Tacrolimus: ${tacrolimusDose}mg BID, Everolimus: ${everolimusDose}mg OD`,
      });
    } catch (error: any) {
      console.error('Error saving medication doses:', error);
      toast.error('Failed to update medication doses', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
    }
  };

  // ✅ NEW: Handler to save clinical note
  const handleSaveClinicalNote = () => {
    if (!note || note.trim() === '') {
      toast.error('Please enter a note before saving');
      return;
    }

    createNoteMutation.mutate({
      patient_id: parseInt(patient.id),
      notes: note
    }, {
      onSuccess: () => {
        setNote('');
      }
    });
  };

  const getExerciseTypeIcon = (type: string) => {
    switch (type) {
      case 'Breathing':
        return <Wind className="w-4 h-4" />;
      case 'Cardio':
        return <Heart className="w-4 h-4" />;
      case 'Strength':
        return <Dumbbell className="w-4 h-4" />;
      case 'Stretching':
        return <Activity className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getIntensityColor = (intensity: string) => {
    switch (intensity) {
      case 'Low':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Moderate':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'High':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Transform vitals data from API for charts
  const getVitalsData = (name:string) => {
    // ✅ FIX: Handle various vitalsData structures
    // Case 1: vitalsData is null/undefined
    if (!vitalsData) {
      console.log('⚠️ No vitals data available (undefined/null), returning empty array');
      return [];
    }
    
    // Case 2: vitalsData has .data property (expected structure)
    if (vitalsData.data && Array.isArray(vitalsData.data)) {
      if (vitalsData.data.length === 0) {
        console.log('⚠️ Vitals data array is empty, returning empty array');
        return [];
      }

      return vitalsData.data.map(v => {
          const item:any = {
            date: new Date(v.date).toLocaleDateString('en-US', { 
              month: '2-digit', 
              day: '2-digit' 
            })
          };

          if (v.heart_rate !== undefined) item.heartRate = v.heart_rate;
          if (v.spo2 !== undefined) item.spO2 = v.spo2;
          if (v.systolic !== undefined) item.systolic = v.systolic;
          if (v.diastolic !== undefined) item.diastolic = v.diastolic;
          if (v.temperature !== undefined) item.temp = v.temperature;

          return item;

      });
    }
    
    // Case 3: vitalsData is directly an array (alternate structure)
    if (Array.isArray(vitalsData)) {
      if (vitalsData.length === 0) {
        console.log('⚠️ Vitals data array is empty, returning empty array');
        return [];
      }
      console.log('✅ Vitals data found (direct array):', vitalsData.length, 'readings');
      return vitalsData.map(v => ({
        date: new Date(v.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
        heartRate: v.heart_rate || 0,
        spO2: v.spo2 || 0,
        systolic: v.systolic || 0,
        diastolic: v.diastolic || 0,
        temp: v.temperature || 0,
      }));
    }
    
    // Case 4: Unexpected structure
    console.warn('⚠️ Unexpected vitalsData structure:', vitalsData);
    return [];
  };

  // Use 6MWT data from API - transform to component format
  const getSixMWTReports = () => {
    if (!sixMWTData?.results || sixMWTData.results.length === 0) {
      return [];
    }
    
    return sixMWTData.results.map((test, index) => ({
      id: test.id?.toString() || `6mwt-${index + 1}`,
      testDate: test.test_date || new Date().toISOString().split('T')[0],
      week: index + 1,
      patientInfo: {
        name: patient.name,
        umrNo: test.patient_id?.toString() || 'N/A',
        age: patient.age,
        gender: patient.gender as const,
        height: test.height || 0,
        weight: test.weight || 0,
        bmi: test.bmi || 0,
      },
      vitals: {
        heartRate: {
          baseline: test.heart_rate_baseline || 0,
          duringTest: test.heart_rate_during || 0,
          postTest: test.heart_rate_post || 0,
        },
        bloodPressure: {
          baseline: { 
            systolic: test.bp_systolic_baseline || 0, 
            diastolic: test.bp_diastolic_baseline || 0 
          },
          postTest: { 
            systolic: test.bp_systolic_post || 0, 
            diastolic: test.bp_diastolic_post || 0 
          },
        },
        saturation: {
          baseline: test.spo2_baseline || 0,
          duringTest: test.spo2_during || 0,
          postTest: test.spo2_post || 0,
        },
        borgScale: {
          baseline: test.borg_baseline || '0/10',
          duringTest: test.borg_during || '0/10',
          postTest: test.borg_post || '0/10',
        },
      },
      results: {
        predictedDistance: test.predicted_distance || 0,
        lowerLimitNormal: test.lower_limit_normal || 0,
        actualDistance: test.distance_walked || 0,
        predictedPercentage: test.predicted_percentage || 0,
      },
      sppbScore: test.sppb_score || 'N/A',
      gripStrength: {
        right: test.grip_strength_right || 0,
        left: test.grip_strength_left || 0,
      },
      physiotherapist: {
        name: test.therapist_name || 'Unknown',
        credentials: test.therapist_credentials || 'PHYSIOTHERAPIST',
      },
      status: (test.status as 'approved' | 'pending' | 'rejected') || 'pending',
      notes: test.notes || '',
    }));
  };
  
  // Get 6MWT reports from API
  const sixMWTReports = getSixMWTReports();


  if  (!dischargeSummary) {
      return <LoadingState message="Loading patient details..." />;  
  }

  const resetMedicationForm = () => {
    setMedicationForm({
      id : '',
      name: '',
      dosage: '',
      frequency: '',
      scheduled_times: '',
      notes: ''      
    });
    setEditingMedication(null);
    setShowAddMedicationForm(false);
  };

  const handleAddMedication = async() => {
    if (!medicationForm.name || !medicationForm.dosage || !medicationForm.frequency || !medicationForm.scheduled_times) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newMedication: Medication = {
      id: '0',
      name: medicationForm.name,
      dosage: medicationForm.dosage,
      frequency: medicationForm.frequency,
      scheduled_times: medicationForm.scheduled_times,
      notes: medicationForm.notes || undefined,
    };

    try {      
      await medicationsApi.createMedicationSchedule({
        patient_id: parseInt(patient.id),
        medication_name: medicationForm.name,
        dosage: medicationForm.dosage,
        frequency: medicationForm.frequency,
        scheduled_times: medicationForm.scheduled_times.split(","),
        instructions: medicationForm.notes,
      });
    } catch (error: any) {
      console.error('Error adding medication:', error);
      toast.error('Failed to add medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    setMedications([...medications, newMedication]);
    toast.success('Medication added successfully!', {
      description: `${newMedication.name} has been added to the medication list.`,
    });
    resetMedicationForm();
  };

  const handleUpdateMedication = async() => {
    if (!editingMedication) return;

    if (!medicationForm.name || !medicationForm.dosage || !medicationForm.frequency || !medicationForm.scheduled_times) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {      
      await medicationsApi.updateMedicationSchedule(parseInt(medicationForm.id), {
        medication_name: medicationForm.name,
        dosage: medicationForm.dosage,
        frequency: medicationForm.frequency,
        scheduled_times: medicationForm.scheduled_times.split(","),
        instructions: medicationForm.notes,
      });
    } catch (error: any) {
      console.error('Error updating medication:', error);
      toast.error('Failed to update medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    const updatedMedications = medications.map(med => 
      med.id === editingMedication.id 
        ? {
            ...med,
            id : medicationForm.id,
            name: medicationForm.name,
            dosage: medicationForm.dosage,
            frequency: medicationForm.frequency,
            scheduled_times: medicationForm.scheduled_times,
            notes: medicationForm.notes || undefined,
          }
        : med
    );

    setMedications(updatedMedications);
    toast.success('Medication updated successfully!', {
      description: `${medicationForm.name} has been updated.`,
    });
    resetMedicationForm();
  };

  const handleEditMedication = (medication: Medication) => {
    setEditingMedication(medication);
    setMedicationForm({
      id : medication.id,
      name: medication.name,
      dosage: medication.dosage,
      frequency: medication.frequency,
      scheduled_times: medication.scheduled_times,
      notes: medication.notes || '',
    });
    setShowAddMedicationForm(true);
  };

  const handleDeleteMedication = async(medicationId: string) => {
    const medication = medications.find(med => med.id === medicationId);
    if (!medication) return;

    try {      
      await medicationsApi.deleteMedicationSchedule(parseInt(medicationId));
    } catch (error: any) {
      console.error('Error deleting medication:', error);
      toast.error('Failed to delete medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    setMedications(medications.filter(med => med.id !== medicationId));
    toast.success('Medication deleted successfully!', {
      description: `${medication.name} has been removed from the medication list.`,
    });
  };
 
  if (userRole === null) {
    return <LoadingState message="Loading patient details..." />;
  }

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Top Navigation for Patient Details */}
      <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={onBack} size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Patient List</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-primary">{selectedPatient.name}</h1>
                <p className="text-muted-foreground">
                  {selectedPatient.age} years • {selectedPatient.hospital_name || 'No Cohort'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline" className={getStatusColor(selectedPatient.status)}>
                {selectedPatient.status}
              </Badge>
              <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                {selectedPatient.adherence}% Adherence
              </Badge>
            </div>
          </div>
        </div>
      </div>    

      <div className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList className="w-full flex flex-wrap md:grid md:grid-cols-7 gap-1">
            <TabsTrigger value="summary" className="flex-1 md:flex-none whitespace-nowrap">
              <FileText className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Clinical Summary</span>
              <span className="sm:hidden">Summary</span>
            </TabsTrigger>
            <TabsTrigger value="symptoms" className="flex-1 md:flex-none whitespace-nowrap">
              <Activity className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Symptoms</span>
              <span className="sm:hidden">Symptoms</span>
            </TabsTrigger>
            <TabsTrigger value="6mwt" className="flex-1 md:flex-none whitespace-nowrap">
              <Footprints className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">6MWT Reports</span>
              <span className="sm:hidden">6MWT</span>
            </TabsTrigger>
            <TabsTrigger value="vitals" className="flex-1 md:flex-none whitespace-nowrap">
              <Activity className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Trends</span>
              <span className="sm:hidden">Trends</span>
            </TabsTrigger>
            <TabsTrigger value="medications" className="flex-1 md:flex-none whitespace-nowrap">
              <Pill className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Medications</span>
              <span className="sm:hidden">Meds</span>
            </TabsTrigger>

            {/*             
            <TabsTrigger value="labs" className="flex-1 md:flex-none whitespace-nowrap">
              <FlaskConical className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Lab Results</span>
              <span className="sm:hidden">Labs</span>
            </TabsTrigger> 
            */}

            {/* 
            <TabsTrigger value="nurse-tasks" className="flex-1 md:flex-none whitespace-nowrap">
              <Home className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Home Nurse Tasks</span>
              <span className="sm:hidden">Nurse</span>
            </TabsTrigger> 
            */}
            
            {/*             
            <TabsTrigger value="notes" className="flex-1 md:flex-none whitespace-nowrap">
              <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Clinical Notes</span>
              <span className="sm:hidden">Notes</span>
            </TabsTrigger> 
            */}
          </TabsList>

          {/* Clinical Summary Tab */}
          <TabsContent value="summary" className="space-y-6">
            <ClinicalSummaryTab 
              patient={patient}
              clinicalNotesData = {clinicalNotesData}
              dischargeSummary={dischargeSummary}
              userRole="physiotherapist"
            />
          </TabsContent>

          {/* Symptoms Tab */}
          <TabsContent value="symptoms" className="space-y-6">
            {symptomsData && symptomsData.symptoms && symptomsData.symptoms.length > 0 ? (
              <Card className="shadow-lg">
                <CardHeader className="bg-primary text-white">
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Symptom History
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Overall Score</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Breathlessness</TableHead>
                        <TableHead>Cough</TableHead>
                        <TableHead>Fatigue</TableHead>
                        <TableHead>Chest Pain</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {symptomsData.symptoms.map((symptom: any) => (
                        <TableRow key={symptom.id}>
                          <TableCell>
                            {new Date(symptom.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              symptom.overall_score < 3 ? 'default' : 
                              symptom.overall_score < 5 ? 'secondary' :
                              symptom.overall_score < 7 ? 'outline' : 'destructive'
                            }>
                              {symptom.overall_score.toFixed(1)}/10
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              symptom.severity === 'mild' ? 'default' :
                              symptom.severity === 'moderate' ? 'secondary' :
                              symptom.severity === 'significant' ? 'outline' : 'destructive'
                            }>
                              {symptom.severity}
                            </Badge>
                          </TableCell>
                          <TableCell>{symptom.symptoms.breathlessness}/10</TableCell>
                          <TableCell>{symptom.symptoms.cough}/10</TableCell>
                          <TableCell>{symptom.symptoms.fatigue}/10</TableCell>
                          <TableCell>{symptom.symptoms.chest_pain}/10</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {symptom.additional_notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-lg">
                <CardContent className="p-12">
                  <EmptyState
                    icon={AlertCircle}
                    title="No Symptoms Available"
                    description="No symptom logs have been recorded for this patient yet."
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* 6MWT Reports Tab */}
          <TabsContent value="6mwt" className="space-y-6">
            <SixMWTReportView
              patientId={patient.id}
              patientName={patient.name}
              reports={sixMWTReports}
              onApprove={(reportId) => {
                toast.success('6MWT Report approved!', {
                  description: 'Report has been signed off successfully',
                });
              }}
              onReject={(reportId) => {
                toast.warning('Re-assessment requested', {
                  description: 'Patient will be notified to perform the test again',
                });
              }}
            />
          </TabsContent>

          {/* Vitals Trends Tab */}
          <TabsContent value="vitals" className="space-y-6">
            {/* ✅ Show empty state if no vitals data */}
            {getVitalsData().length === 0 && (
              <Card className="shadow-lg">
                <CardContent className="py-12">
                  <EmptyState
                    icon={Activity}
                    title="No Vitals Data Available"
                    message="This patient hasn't logged any vitals yet. Vitals trends will appear here once data is recorded."
                  />
                </CardContent>
              </Card>
            )}

            {/* FEV1 Trends Card */}
            <FEV1TrendsCard 
              patientId={patient.id}
              patientName={patient.name}
            />

            {/* 6MWD Trends Card */}
            <SixMWDTrendsCard 
              patientId={patient.id}
              patientName={patient.name}
            />

            {/* Show vitals charts only if data exists */}
            {getVitalsData().length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SpO2 Trend */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary flex items-center gap-2">
                      <Droplet className="w-5 h-5" />
                      SpO2 Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getVitalsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis dataKey="date" stroke="#494949" />
                          <YAxis domain={[90, 100]} stroke="#494949" />
                          <Tooltip />
                          <ReferenceLine y={95} stroke="#E84C3D" strokeDasharray="5 5" label="Threshold" />
                          <Line
                            type="monotone"
                            dataKey="spO2"
                            stroke="#14CAF4"
                            strokeWidth={2}
                            dot={{ fill: '#14CAF4', r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

              {/* Heart Rate Trend */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Heart Rate Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getVitalsData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                        <XAxis dataKey="date" stroke="#494949" />
                        <YAxis domain={[60, 90]} stroke="#494949" />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="heartRate"
                          stroke="#F07D13"
                          strokeWidth={2}
                          dot={{ fill: '#F07D13', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Blood Pressure Trend */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Blood Pressure Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getVitalsData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                        <XAxis dataKey="date" stroke="#494949" />
                        <YAxis domain={[70, 130]} stroke="#494949" />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="systolic"
                          stroke="#2762AC"
                          strokeWidth={2}
                          dot={{ fill: '#2762AC', r: 4 }}
                          name="Systolic"
                        />
                        <Line
                          type="monotone"
                          dataKey="diastolic"
                          stroke="#14CAF4"
                          strokeWidth={2}
                          dot={{ fill: '#14CAF4', r: 4 }}
                          name="Diastolic"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Temperature Trend */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <ThermometerSun className="w-5 h-5" />
                    Temperature Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getVitalsData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                        <XAxis dataKey="date" stroke="#494949" />
                        <YAxis domain={[36, 38]} stroke="#494949" />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="temp"
                          stroke="#F07D13"
                          strokeWidth={2}
                          dot={{ fill: '#F07D13', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>

          {/* Lab Results Tab */}
          <TabsContent value="labs" className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">
                  <span className="text-base sm:text-lg">Lab Test Results</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Test Name</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Reference Range</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>Blood Test - Complete</TableCell>
                          <TableCell>2025-10-08</TableCell>
                          <TableCell>WBC: 7.2 k/μL</TableCell>
                          <TableCell>4.5-11.0 k/μL</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                              Normal
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Chest X-Ray</TableCell>
                          <TableCell>2025-09-25</TableCell>
                          <TableCell>Clear lungs</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                              Normal
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>ABG Analysis</TableCell>
                          <TableCell>2025-09-20</TableCell>
                          <TableCell>pH: 7.38</TableCell>
                          <TableCell>7.35-7.45</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                              Normal
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lab Results Trends */}
            <div className="mt-6">
              <LabResultsTrends 
                patientId={patient.id}
                patientName={patient.name}
              />
            </div>
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications" className="space-y-6">
            {/* Add/Edit Medication Form */}
            {(userRole===UserRole.PHYSICIAN_ASSISTANT &&  showAddMedicationForm) && (
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">
                    {editingMedication ? 'Edit Medication' : 'Add New Medication'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="medication-name" className="text-muted-foreground">
                        Medication Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="medication-name"
                        placeholder="e.g., Tacrolimus"
                        value={medicationForm.name}
                        onChange={(e) => setMedicationForm({ ...medicationForm, name: e.target.value })}
                        className="border-primary/30 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="medication-dosage" className="text-muted-foreground">
                        Dosage <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="medication-dosage"
                        placeholder="e.g., 2.5 mg"
                        value={medicationForm.dosage}
                        onChange={(e) => setMedicationForm({ ...medicationForm, dosage: e.target.value })}
                        className="border-primary/30 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="medication-frequency" className="text-muted-foreground">
                        Frequency <span className="text-red-500">*</span>
                      </Label>

                      <select
                        id="medication-frequency"
                        value={medicationForm.frequency}
                        onChange={(e) =>
                          setMedicationForm({ ...medicationForm, frequency: e.target.value })
                        }
                        className="w-full border border-primary/30 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select frequency</option>
                        <option value="once_daily">Once Daily</option>
                        <option value="twice_daily">Twice Daily</option>
                        <option value="three_times_daily">Three Times Daily</option>
                        <option value="four_times_daily">Four Times Daily</option>
                        <option value="as_needed">As Needed</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="medication-frequency" className="text-muted-foreground">
                        Scheduled Times <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="scheduled-times"
                        placeholder="2 PM, 8PM"
                        value={medicationForm.scheduled_times}
                        onChange={(e) => setMedicationForm({ ...medicationForm, scheduled_times: e.target.value })}
                        className="border-primary/30 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="medication-notes" className="text-muted-foreground">
                        Notes (Optional)
                      </Label>
                      <textarea
                        id="medication-notes"
                        placeholder="e.g., Take with food. Monitor blood levels regularly."
                        value={medicationForm.notes}
                        onChange={(e) => setMedicationForm({ ...medicationForm, notes: e.target.value })}
                        className="w-full p-3 border border-primary/30 focus:border-primary rounded-xl min-h-20"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button 
                      variant="outline"
                      onClick={resetMedicationForm}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="bg-primary hover:bg-primary/90"
                      onClick={editingMedication ? handleUpdateMedication : handleAddMedication}
                    >
                      {editingMedication ? 'Update Medication' : 'Add Medication'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Medications List */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-primary">Current Medications</CardTitle>
                  {!showAddMedicationForm && userRole===UserRole.PHYSICIAN_ASSISTANT && (
                    <Button 
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => setShowAddMedicationForm(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Medication
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {medications.length === 0 ? (
                  <div className="text-center py-12">
                    <Pill className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg text-muted-foreground mb-2">No Medications</h3>
                    <p className="text-muted-foreground mb-4">
                      Start by adding medications for this patient.
                    </p>
                    <Button 
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => setShowAddMedicationForm(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Medication
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Medication Name</TableHead>
                            <TableHead>Dosage</TableHead>
                            <TableHead>Frequency</TableHead> 
                            <TableHead>Scheduled Times</TableHead>                            
                            {role===UserRole.PHYSICIAN_ASSISTANT &&  <TableHead>Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {medications.map((medication) => (
                            <TableRow key={medication.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-primary">{medication.name}</p>
                                  {medication.notes && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {medication.notes}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{medication.dosage}</TableCell>
                              <TableCell>{medication.frequency}</TableCell>
                              <TableCell>{medication.scheduled_times}</TableCell>
                              { role===UserRole.PHYSICIAN_ASSISTANT &&  
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditMedication(medication)}
                                    className="text-primary hover:text-primary/80"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteMedication(medication.id)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                              }
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Home Nurse Tasks Tab */}
          <TabsContent value="nurse-tasks" className="space-y-6">
            <HomeNurseTasksViewCompact
              patientId={patient.id}
              patientName={patient.name}
              patientAge={patient.age}
              patientGender={patient.gender}
              diagnosis={dischargeSummary?.diagnosis || 'Post-Lung Transplant'}
            />
          </TabsContent>
            
          {/* Clinical Notes Tab */}
          <TabsContent value="notes" className="space-y-6">
            {/* Immunosuppressant Medication Management */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Immunosuppressant Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tacrolimus Section */}
                <div className="p-4 bg-primary/5 rounded-xl space-y-4">
                  <h3 className="text-primary">Tacrolimus</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tacrolimus-level" className="text-muted-foreground">
                        Current Level
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="tacrolimus-level"
                          value={tacrolimusLevel}
                          disabled
                          className="bg-secondary"
                        />
                        <span className="text-muted-foreground whitespace-nowrap">ng/mL</span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Target range: 5-15 ng/mL
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tacrolimus-dose" className="text-muted-foreground">
                        Dose Adjustment
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="tacrolimus-dose"
                          type="number"
                          step="0.5"
                          value={tacrolimusDose}
                          onChange={(e) => setTacrolimusDose(e.target.value)}
                          className="border-primary/30 focus:border-primary"
                        />
                        <span className="text-muted-foreground whitespace-nowrap">mg</span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Current dose: {tacrolimusDose} mg BID
                      </p>
                    </div>
                  </div>
                </div>

                {/* Everolimus Section */}
                <div className="p-4 bg-[#14CAF4]/5 rounded-xl space-y-4">
                  <h3 className="text-primary">Everolimus</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="everolimus-level" className="text-muted-foreground">
                        Current Level
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="everolimus-level"
                          value={everolimusLevel}
                          disabled
                          className="bg-secondary"
                        />
                        <span className="text-muted-foreground whitespace-nowrap">ng/mL</span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Target range: 3-8 ng/mL
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="everolimus-dose" className="text-muted-foreground">
                        Dose Adjustment
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="everolimus-dose"
                          type="number"
                          step="0.5"
                          value={everolimusDose}
                          onChange={(e) => setEverolimusDose(e.target.value)}
                          className="border-[#14CAF4]/30 focus:border-[#14CAF4]"
                        />
                        <span className="text-muted-foreground whitespace-nowrap">mg</span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Current dose: {everolimusDose} mg OD
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setTacrolimusDose('2.5');
                      setEverolimusDose('1.0');
                      toast.info('Doses reset to previous values');
                    }}
                  >
                    Reset
                  </Button>
                  <Button 
                    className="bg-primary hover:bg-primary/90"
                    onClick={handleSaveMedicationDoses}
                  >
                    Update Doses
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Add Clinical Note</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <textarea
                  className="w-full p-4 border border-border rounded-xl min-h-32"
                  placeholder="Enter clinical observations, treatment adjustments, or recommendations..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setNote('');
                      toast.info('Note discarded');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="bg-accent hover:bg-accent/90"
                    onClick={handleSaveClinicalNote}
                    disabled={createNoteMutation.isPending}
                  >
                    {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Previous Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {notesLoading ? (
                  <LoadingState message="Loading clinical notes..." />
                ) : notesError ? (
                  <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
                    <p>Error loading notes: {(notesError as any)?.message || 'Unknown error'}</p>
                  </div>
                ) : clinicalNotesData?.notes && clinicalNotesData.notes.length > 0 ? (
                  clinicalNotesData.notes.map((noteItem) => (
                    <div key={noteItem.id} className="p-4 border border-border rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-primary">Dr. {noteItem.doctor_name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-muted-foreground text-sm">
                            {new Date(noteItem.created_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </p>
                          {user?.id === noteItem.created_by && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingNoteId(noteItem.id);
                                  setEditingNoteText(noteItem.notes);
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this note?')) {
                                    deleteNoteMutation.mutate({
                                      noteId: noteItem.id,
                                      patientId: parseInt(patient.id)
                                    });
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {editingNoteId === noteItem.id ? (
                        <div className="space-y-2">
                          <textarea
                            className="w-full p-3 border border-border rounded-lg min-h-24"
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingNoteText('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="bg-accent hover:bg-accent/90"
                              onClick={() => {
                                if (!editingNoteText.trim()) {
                                  toast.error('Note cannot be empty');
                                  return;
                                }
                                updateNoteMutation.mutate({
                                  noteId: noteItem.id,
                                  data: { notes: editingNoteText },
                                  patientId: parseInt(patient.id)
                                }, {
                                  onSuccess: () => {
                                    setEditingNoteId(null);
                                    setEditingNoteText('');
                                  }
                                });
                              }}
                              disabled={updateNoteMutation.isPending}
                            >
                              {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-foreground whitespace-pre-wrap">{noteItem.notes}</p>
                      )}
                      {noteItem.updated_at && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Last updated: {new Date(noteItem.updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <EmptyState
                    title="No clinical notes yet"
                    description="Add your first clinical note above to start tracking patient progress."
                    icon={FileText}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}